import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { AfterViewInit, Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Color } from '@amcharts/amcharts4/.internal/core/utils/Color';
import { CurrencyService } from '@app/shared/services/currency/currency.service';
import { LegendPosition } from '@amcharts/amcharts4/.internal/charts/Legend';
import {
  ChartDataInterface,
  ChartDataType,
  ChartTheme,
} from '@app/shared/components/charts/interfaces/chart.interface';
import { ChartHelper } from '@app/shared/components/charts/helpers/chart.helper';

am4core.useTheme(am4themes_animated);

@Component({
  selector: 'generic-stacked-bar-chart',
  templateUrl: './generic-stacked-bar-chart.component.html',
  styleUrls: ['./generic-stacked-bar-chart.component.scss'],
})
export class GenericStackedBarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() title = '';
  @Input() chartId = 'stackedBarChart';

  @Input() set data(value: ChartDataInterface[]) {
    if (typeof value !== 'undefined') {
      this.chartData = value.map(item => ({ ...item }));
    }
  }

  @Input() series: string[];
  @Input() chartHeight = '600px';
  @Input() dataType: ChartDataType = ChartDataType.NUMBER;
  @Input() rotateLabels = false;
  @Input() colorsList: Color[] = [
    am4core.color('#37BC9B'),
    am4core.color('#DA4453'),
    am4core.color('#967ADC'),
    am4core.color('#ffa509'),
    am4core.color('#ffce00'),
    am4core.color('#5f9bd9'),
    am4core.color('#483334'),
    am4core.color('#4be38f'),
    am4core.color('#dfcc64'),
    am4core.color('#8193df'),
    am4core.color('#d1392e'),
    am4core.color('#8f8559'),
    am4core.color('#eb7cca'),
    am4core.color('#1a8777'),
  ];

  @Input() theme: ChartTheme = ChartTheme.WHITE;
  @Input() legend = true;
  @Input() legendPosition: LegendPosition = 'top';

  private availableDataTypes: typeof ChartDataType = ChartDataType;
  private chart: am4charts.XYChart;
  private chartData: ChartDataInterface[] = [];

  constructor(
    private readonly zone: NgZone,
    private readonly currencyService: CurrencyService,
    private readonly decimalPipe: DecimalPipe,
    private readonly chartHelper: ChartHelper,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const chartDataChange = changes['data'];
    if (chartDataChange && typeof chartDataChange.previousValue !== 'undefined') {
      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      if (this.chart) {
        this.chart.dispose();
      }
    });
  }

  private createChart(): void {
    am4core.unuseAllThemes();
    if (this.dataType === this.availableDataTypes.PERCENTAGE) {
      this.chartData.forEach((item, index) => {
        const values = item.values.map(value => value * 100);
        this.chartData[index].values = values;
      });
    }
    if (this.dataType === this.availableDataTypes.CURRENCY) {
      this.chartData.forEach((item, index) => {
        const values = item.values.map(value => Math.round((value + Number.EPSILON) * 100) / 100);
        this.chartData[index].values = values;
      });
    }
    this.zone.runOutsideAngular(() => {
      this.zone.runOutsideAngular(() => {
        const chart = am4core.create(this.chartId, am4charts.XYChart);
        chart.data = this.chartHelper.prepareChartData(this.chartData, this.series);
        chart.colors.list = this.colorsList;

        const title = chart.titles.create();
        title.text = this.title;
        title.fontSize = 20;
        title.marginBottom = 10;
        title.marginTop = 10;
        title.align = 'center';

        const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'seriesName';
        if (this.rotateLabels) {
          categoryAxis.renderer.labels.template.rotation = 270;
          categoryAxis.renderer.labels.template.horizontalCenter = 'right';
          categoryAxis.renderer.labels.template.verticalCenter = 'middle';
          categoryAxis.renderer.labels.template.wrap = true;
          categoryAxis.renderer.labels.template.maxWidth = 140;

        } else {
          categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
          categoryAxis.renderer.labels.template.verticalCenter = 'middle';
        }
        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.renderer.grid.template.disabled = true;
        categoryAxis.renderer.minGridDistance = 30;
        categoryAxis.renderer.grid.template.fill =
          this.chartHelper.getThemeColor(this.theme);
        categoryAxis.renderer.labels.template.fill = this.chartHelper.getThemeColor(this.theme);

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.grid.template.disabled = true;
        valueAxis.renderer.grid.template.fill =
          this.chartHelper.getThemeColor(this.theme);
        valueAxis.renderer.labels.template.fill =
          this.chartHelper.getThemeColor(this.theme);
        valueAxis.renderer.baseGrid.stroke =
          this.chartHelper.getThemeColor(this.theme);
        if (this.dataType === this.availableDataTypes.PERCENTAGE) {
          valueAxis.renderer.labels.template.adapter.add('text', (text, label) => {
            return label.dataItem['value'] + '%';
          });
          valueAxis.max = 100;
        }
        if (this.dataType === this.availableDataTypes.CURRENCY) {
          valueAxis.renderer.labels.template.adapter.add('text', (text, label) => {
            return this.currencyService.currencySymbol + this.decimalPipe.transform(label.dataItem['value'], '1.');
          });
        }
        if (this.dataType === this.availableDataTypes.NUMBER) {
          valueAxis.renderer.labels.template.adapter.add('text', (text, label) => {
            return this.decimalPipe.transform(label.dataItem['value'], '1.');
          });
        }
        if (!this.hasNegativeValues()) {
          valueAxis.min = 0;
        }

        const createSeries = (name: string): am4charts.ColumnSeries => {
          const series = chart.series.push(new am4charts.ColumnSeries());
          series.name = name;
          series.dataFields.valueY = name;
          series.dataFields.categoryX = 'seriesName';
          series.sequencedInterpolation = true;
          series.stacked = true;

          series.columns.template.width = am4core.percent(60);
          const labelBullet = series.bullets.push(new am4charts.LabelBullet());
          labelBullet.label.adapter.add('text', (text, target) => {
            return target.dataItem && target.dataItem['valueY'] !== 0 ? '{valueY.formatNumber("#.#")}%' : void 0;
          });
          labelBullet.locationY = 0.5;
          labelBullet.label.fill = am4core.color('#fff');
          series.columns.template.adapter.add('tooltipText', this.getHorizontalTooltipText.bind(this));

          return series;
        };

        this.series.forEach(seriesName => {
          createSeries(seriesName);
        });

        if (this.legend) {
          chart.legend = new am4charts.Legend();
          chart.legend.position = this.legendPosition;
          chart.legend.labels.template.fill = this.chartHelper.getThemeColor(this.theme);
        }

        this.chart = chart;
      });
    });
  }

  private hasNegativeValues(): boolean {
    let hasNegative = false;
    this.chartData.forEach(element => {
      hasNegative = element.values.some(item => item < 0);
    });
    return hasNegative;
  }

  private getHorizontalTooltipText(_text, target): string {
    const type = target.tooltipDataItem.dataContext.type || this.dataType;
    if (type === this.availableDataTypes.PERCENTAGE) {
      return '[font-size:14px]{name}: {valueY.formatNumber("#.#")}%';
    }
    if (type === this.availableDataTypes.CURRENCY) {
      return `[font-size:14px]{name}: ${this.currencyService.currencySymbol}{valueY}`;
    }
    return '[font-size:14px]{name}: {valueY}';
  }
}
